import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { translationPropType } from '@eb/i18n';
import classNames from 'classnames';

import { Graphic } from '@eb/eds-graphic';
import { GRAPHIC_TYPE_PROP_TYPE } from '@eb/eds-graphic';
import { Button } from '@eb/eds-button';
import * as constants from './constants';
import { classRange } from '@eb/eds-utils';

import './emptyState.scss';

const Title = ({ children, isDisabled }) => {
    let component = null;

    if (children) {
        const responsiveMargin = `${classRange(
            'mar-top-6',
            'sn',
            'md',
        )} ${classRange('mar-top-8', 'md')}`;
        const className = classNames(
            'eds-empty-state__title',
            'eds-text--center',
            'eds-text-hs',
            {
                'eds-text-color--grey-900': !isDisabled,
                'eds-text-color--grey-500': isDisabled,
            },
            responsiveMargin,
        );

        component = <h1 className={className}>{children}</h1>;
    }

    return component;
};

const Description = ({ children, hasTitle, isDisabled }) => {
    let component = null;

    if (children) {
        const responsiveMargin = `${classRange(
            'mar-top-3',
            'sn',
            'md',
        )} ${classRange('mar-top-1', 'md')}`;
        const responsiveMarginNoTitle = `${classRange(
            'mar-top-6',
            'sn',
            'md',
        )} ${classRange('mar-top-8', 'md')}`;
        const className = classNames(
            'eds-empty-state__description',
            'eds-text--center',
            'eds-text-bl',
            {
                'eds-text-color--grey-600': !isDisabled,
                'eds-text-color--grey-400': isDisabled,
                [responsiveMargin]: hasTitle,
                [responsiveMarginNoTitle]: !hasTitle,
            },
        );

        component = (
            <div className="eds-align--center">
                <p className={className}>{children}</p>
            </div>
        );
    }

    return component;
};

const Actions = ({ primaryType, primaryText, onPrimaryAction, isDisabled }) => {
    let component = null;

    if (primaryText) {
        const responsiveMargin = `${classRange(
            'mar-top-4',
            'sn',
            'md',
        )} ${classRange('mar-top-6', 'md')}`;
        const className = classNames(
            'eds-empty-state__actions',
            'eds-align--center',
            responsiveMargin,
        );

        component = (
            <div className={className}>
                <Button
                    onClick={onPrimaryAction}
                    style={primaryType}
                    data-spec="eds-empty-state__primary-button"
                    disabled={isDisabled}
                >
                    {primaryText}
                </Button>
            </div>
        );
    }

    return component;
};

export default class EmptyState extends PureComponent {
    static propTypes = {
        /**
         * Graphic to be displayed
         */
        graphicType: GRAPHIC_TYPE_PROP_TYPE,
        /**
         * Title text to be displayed under the graphic
         */
        title: PropTypes.node,
        /**
         * Description text to be displayed under the title
         */
        description: PropTypes.node,
        /**
         * The type of button that should be shown for primary button
         */
        primaryType: PropTypes.oneOf(constants.PRIMARY_TYPES),
        /**
         * The text that should be shown across the primary button
         */
        primaryText: translationPropType,
        /**
         * The function that should be triggered on primary button click
         */
        onPrimaryAction: PropTypes.func,
        /**
         * Disables the centering of the empty state container
         */
        disableCentering: PropTypes.bool,
        /**
         * Disable Empty state
         */
        isDisabled: PropTypes.bool,
    };

    static defaultProps = {
        graphicType: constants.DEFAULT_GRAPHIC,
        primaryType: constants.DEFAULT_TYPE,
        isDisabled: false,
    };

    render() {
        const {
            graphicType,
            description,
            primaryType,
            primaryText,
            onPrimaryAction,
            disableCentering,
            title,
            isDisabled,
        } = this.props;

        const className = classNames('eds-empty-state', {
            'eds-align--center': !disableCentering,
        });

        return (
            <div className={className} data-spec="empty-state">
                <div>
                    <div className="eds-empty-state__graphic eds-align--center">
                        <Graphic
                            hasHalo={true}
                            haloSize="xsmall"
                            type={graphicType}
                            size="small"
                        />
                    </div>
                    <Title isDisabled={isDisabled}>{title}</Title>
                    <Description hasTitle={!!title} isDisabled={isDisabled}>
                        {description}
                    </Description>
                    <Actions
                        primaryType={primaryType}
                        primaryText={primaryText}
                        onPrimaryAction={onPrimaryAction}
                        isDisabled={isDisabled}
                    />
                </div>
            </div>
        );
    }
}
